'use client'

import { useEffect, useRef, useState } from 'react'

import { IconCheck, IconChevDown, IconSearch } from '@/app/components/atoms/Icons'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { slugify } from '@/app/lib/analytics/dataLayer'
import { PROVINCES } from '@/app/lib/constants'
import { cx } from '@/app/lib/utils'

import styles from './LocationPicker.module.scss'
import { defaultLabelForLocation } from './defaultLabelForLocation'
import type { LocationPickerProps } from './LocationPicker.types'

function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

/**
 * Selector de localización compartido: botón + desplegable con buscador,
 * alimentado por la fuente canónica `PROVINCES`. Mismo comportamiento de UI
 * que el `CityPicker` de la referencia (`eci-firefly-uikit`), reimplementado
 * con átomos y estilos propios de este proyecto.
 *
 * Promovido a `shared/` (antes colocado en `modules/ProductosLocalizados/`)
 * cuando el módulo `Agenda` pasó a necesitar el mismo selector
 * (specs/042-modulo-agenda-cms) — Principio II: "local primero, global solo
 * con reutilización real". La etiqueta visible de cada slug se resuelve vía
 * `getLabel` (por defecto, la etiqueta canónica de `PROVINCES`) para que un
 * consumidor con una etiqueta de presentación distinta (p. ej.
 * `ProductosLocalizadosHeader`, que muestra "toda España" en vez de "España
 * (todas)" para `ALL_LOCATIONS`) no tenga que vivir aquí como caso especial.
 */
export function LocationPicker({
  location,
  onSelect,
  getLabel = defaultLabelForLocation,
  analytics,
  inverse = false,
}: LocationPickerProps) {
  const pageAnalytics = useOptionalPageAnalytics()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // `ui_interaction` de apertura del desplegable (`Grid Cards.csv` →
  // "Apertura del dropdown") — solo en la transición a abierto, el CSV no
  // documenta una fila de "cierre" para este selector concreto (a diferencia
  // del menú móvil de `Header.csv`, que sí documenta apertura/cierre juntos).
  function handleToggleOpen() {
    const willOpen = !open
    setOpen(willOpen)
    if (!willOpen || !analytics) return
    pageAnalytics?.trackUiInteraction({
      component_type: 'dropdown',
      component_action: 'open',
      component_id: analytics.componentId,
      component_label: analytics.componentLabel,
      selected_value: NO_APLICA,
      module_name: analytics.moduleName,
      // `module_type`/`module_position`: valores LITERALES de la fila
      // "Apertura del dropdown" de `Grid Cards.csv`.
      module_type: 'province_selector',
      module_position: NO_APLICA,
    })
  }

  /** Cierra el desplegable devolviendo el foco al botón que lo abrió — mismo
   * criterio que un disclosure/listbox (`.claude/rules/accessibility.rule.md`,
   * "Modal closes → focus returns to trigger"), aunque no haya trampa de
   * foco: solo el botón permanece en el DOM tras desmontar el desplegable. */
  function close({ refocusTrigger }: { refocusTrigger: boolean }) {
    setOpen(false)
    setQuery('')
    if (refocusTrigger) triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return

    function onPointerDown(event: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) {
        // Clic fuera: el foco del usuario ya se dirige a otro punto de la
        // página — no se le arrebata para devolverlo al botón.
        close({ refocusTrigger: false })
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        close({ refocusTrigger: true })
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const filtered = query.trim()
    ? PROVINCES.filter((province) => normalize(getLabel(province.slug)).includes(normalize(query.trim())))
    : PROVINCES

  function handleSelect(slug: string) {
    if (analytics) {
      pageAnalytics?.trackUiInteraction({
        component_type: 'dropdown',
        component_action: 'select',
        component_id: analytics.componentId,
        component_label: analytics.componentLabel,
        selected_value: slugify(slug),
        module_name: analytics.moduleName,
        // `module_type`/`module_position`: valores LITERALES de la fila
        // "Selección de una provincia" de `Grid Cards.csv` — a diferencia de
        // la fila de apertura, aquí el propio CSV fija `module_type: 'no_aplica'`.
        module_type: NO_APLICA,
        module_position: NO_APLICA,
      })
    }
    onSelect(slug)
    close({ refocusTrigger: true })
  }

  return (
    <div ref={wrapRef} className={styles.root}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggleOpen}
        className={cx(styles.trigger, inverse && styles.triggerInverse)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {getLabel(location)}
        <span className={cx(styles.chevron, open && styles.chevronOpen)}>
          <IconChevDown size={20} />
        </span>
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap}>
            <div className={styles.searchField}>
              <span className={styles.searchIcon}>
                <IconSearch size={18} />
              </span>
              <input
                autoFocus
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Busca provincia o ciudad"
                aria-label="Busca provincia o ciudad"
                className={styles.searchInput}
              />
            </div>
          </div>

          <ul className={styles.list} role="listbox" aria-label="Localizaciones">
            {filtered.length === 0 && <li className={styles.noResults}>Sin resultados</li>}
            {filtered.map((province) => (
              // `role="option"`/`aria-selected` van en el propio `<button>`, no en el
              // `<li>`: un `role="option"` que envuelve otro elemento interactivo
              // (el `<button>`) es un anti-patrón ARIA (doble semántica, confuso
              // para lectores de pantalla) y además rompe el click real de un
              // usuario/test que apunte al `option` — el evento nace en el `<li>`
              // y nunca desciende al `<button>` hijo.
              <li key={province.slug}>
                <button
                  type="button"
                  role="option"
                  aria-selected={province.slug === location}
                  onClick={() => handleSelect(province.slug)}
                  className={styles.optionButton}
                  data-selected={province.slug === location ? '' : undefined}
                >
                  <span>{getLabel(province.slug)}</span>
                  {province.slug === location && (
                    <span className={styles.checkIcon}>
                      <IconCheck size={18} />
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
